/**
 * DetailSubNav.tsx
 *
 * The chart's section navigation. A collapsible rail at `lg` and above; a horizontal
 * scrolling strip below it.
 *
 * ── Why it reflows rather than going off-canvas ──────────────────────────────────
 * It is the primary way around a patient record, so hiding it behind a tap would cost
 * more than the width it takes. An icon-only rail was the other option and is worse in
 * the 768–1023 band specifically: the shadcn menu button only shows its tooltip when
 * the viewport is mobile, so a rail there would render as unlabelled icons with no
 * hover text — and that band is exactly tablet portrait.
 *
 * ── Collapse state comes from the layout, through a context ─────────────────────
 * The sidebar context this component used to read is shared with the global
 * navigation, and one persisted key backs all of it — so collapsing this rail
 * persisted "global nav collapsed" for the next page load. Its toggle was also a
 * no-op below 768, because it routes to a mobile-open flag nothing in this subtree
 * renders.
 *
 * A context rather than props: pages pass this component to `DetailPageLayout` as an
 * already-constructed node, so the layout has nothing to attach props to. When no
 * provider is present — the `v5` copy, or any other caller — it falls back to the
 * sidebar context and behaves exactly as it did before.
 */
import { CollapseChevron } from "@/components/ui/collapse-chevron";
import {
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { createContext, useContext, useRef, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { scrollElementIntoViewX } from "@/v5/lib/scrollWithin";

export interface SubNavSection {
  key: string;
  label: string;
  icon: LucideIcon;
}

export interface SubNavCollapseState {
  collapsed: boolean;
  toggle: () => void;
}

const SubNavCollapseContext = createContext<SubNavCollapseState | null>(null);

/**
 * Hands this rail a collapse state that is its own. Provided by `DetailPageLayout`;
 * memoize the value, since it sits above the whole chart.
 */
export function DetailSubNavCollapseProvider({
  value,
  children,
}: {
  value: SubNavCollapseState;
  children: ReactNode;
}) {
  return <SubNavCollapseContext.Provider value={value}>{children}</SubNavCollapseContext.Provider>;
}

interface DetailSubNavProps {
  sections: SubNavSection[];
  activeSection: string;
  onSectionChange: (key: string) => void;
  header: React.ReactNode;
}

export function DetailSubNav({
  sections,
  activeSection,
  onSectionChange,
  header,
}: DetailSubNavProps) {
  const { state, toggleSidebar } = useSidebar();
  const owned = useContext(SubNavCollapseContext);
  const isStrip = useIsBelow("lg");
  // Above the `isStrip` return on purpose. It is only read in the strip branch, but a
  // hook below an early return runs a different number of times per render and throws
  // React #310 — the failure that blanked every page in this effort once already.
  const stripRef = useRef<HTMLDivElement>(null);
  const isCollapsed = owned ? owned.collapsed : state === "collapsed";
  const toggle = owned ? owned.toggle : toggleSidebar;

  if (isStrip) {
    // Same overflow idiom as the shared Tabs component: a scroller with the bar
    // hidden, items that refuse to shrink, and labels that never wrap. Collapse does
    // not apply to a strip, so the chevron is not rendered rather than rendered dead.
    return (
      <div className="flex w-full min-w-0 flex-col bg-white text-slate-800">
        <div className="border-b border-slate-200 px-3 py-3">{header}</div>

        <div
          ref={stripRef}
          role="tablist"
          aria-label="Record sections"
          className="flex overflow-x-auto scrollbar-hide"
        >
          {sections.map(({ key, label, icon: Icon }) => {
            const isActive = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                // Carries the active state to assistive tech. The underline alone
                // conveys it to sighted users and to nobody else.
                aria-selected={isActive}
                onClick={() => onSectionChange(key)}
                // Tabbing to an item that has scrolled out of the strip has to bring
                // it into view, or keyboard focus lands somewhere invisible.
                //
                // Scoped to the strip. This was `scrollIntoView({ block: "nearest",
                // inline: "nearest" })`, and `inline` scrolls every scrollable ancestor
                // on the x-axis — including `overflow-hidden` shells, which are still
                // programmatically scrollable, and below `md` the document itself. So
                // focusing a tab (which a tap does) slid the whole chart sideways, and
                // the patient header, id, Details button and tab row all read as cut
                // off. Nine tickets across two batches described that symptom.
                //
                // SCRUM-1362 recorded this exact mechanism and wrote scrollWithin for
                // it; this strip shipped in the same batch without adopting it.
                onFocus={(e) => {
                  const strip = stripRef.current;
                  if (strip) scrollElementIntoViewX(strip, e.currentTarget);
                }}
                className={cn(
                  "relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-600 hover:text-slate-900",
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group relative z-10 flex h-full min-h-0 flex-shrink-0 self-stretch overflow-visible border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-linear",
        isCollapsed ? "w-14" : "w-64",
      )}
      data-state={isCollapsed ? "collapsed" : "expanded"}
      data-collapsible={isCollapsed ? "icon" : ""}
      data-side="left"
      data-variant="sidebar"
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        <SidebarHeader className="px-3 py-4 border-b border-sidebar-border">
          {header}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {sections.map(({ key, label, icon: Icon }) => (
                <SidebarMenuItem key={key}>
                  <SidebarMenuButton
                    isActive={activeSection === key}
                    tooltip={label}
                    onClick={() => onSectionChange(key)}
                    className={cn(
                      activeSection === key &&
                        "data-[active=true]:bg-slate-900 data-[active=true]:text-white data-[active=true]:hover:bg-slate-900 data-[active=true]:hover:text-white data-[active=true]:active:bg-slate-900 data-[active=true]:active:text-white",
                    )}
                  >
                    <Icon />
                    <span>{label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </div>

      <CollapseChevron
        collapsed={isCollapsed}
        onClick={toggle}
        side="left"
        title={isCollapsed ? "Expand sub-nav" : "Collapse sub-nav"}
      />
    </div>
  );
}
